import Phaser from 'phaser';
import { t } from '@i18n/index';
import {
  PALETTE,
  TEXT_COLOUR,
  titleStyle,
  readoutStyle,
  labelStyle,
  FONT,
  px,
  getReducedMotion,
  motionMs,
} from '../theme';
import { createButton } from '../ui/button';
import { layoutOf, watchResize, type Layout } from '../ui/layout';
import { createScrollArea, type ScrollArea } from '../ui/scrollArea';
import { incidentType } from '@core/mission/incident';
import type { MissionResult } from '@core/campaign/campaign';
import type { OutcomeTier } from '@core/mission/outcome';
import type { CollectedClue } from '@core/mystery/ledger';
import {
  createSonarSearch,
  FIELD_METRES,
  FIND_RADIUS_METRES,
  HOURS_PER_PING,
  type Point,
  type SonarSearch,
} from '@core/mission/sonarSearch';
import { audio } from '../audio/engine';
import { DRONE_ROOT, SITE_MOTIF } from '../audio/motifs';
import { maybeShowTutorial } from '../ui/adviserPanel';
import { plateKey, TIER_PLATE } from '../assets';
import type { Campaign } from '@core/campaign/campaign';
import type { VesselId } from '@core/voyage/profiles';

const TIER_COLOUR: Record<OutcomeTier, string> = {
  rescue: TEXT_COLOUR.base,
  salvage: TEXT_COLOUR.landmark,
  cold: PALETTE.inkDim,
  nothing: TEXT_COLOUR.danger,
};

/** Wie die Stufe auch ohne Farbe lesbar ist — siehe Kartenmarker. */
const TIER_GLYPH: Record<OutcomeTier, string> = {
  rescue: '●',
  salvage: '■',
  cold: '◇',
  nothing: '✕',
};

type SiteResult = MissionResult & { stranded: boolean; vesselId: VesselId };
type Phase = 'search' | 'result';
type SearchOutcome = 'found' | 'missed' | 'skipped' | null;

/**
 * Was am Einsatzort vorgefunden wurde.
 *
 * Der Moment, in dem sich die Entscheidungen der Reise auszahlen — oder eben
 * nicht. Die geborgenen Hinweise werden hier zum ersten Mal im Wortlaut
 * gezeigt; sie sind der eigentliche Ertrag der Mission.
 *
 * Bei Bergung und kalter Spur kommt vorher die Suche: Das Wrack liegt nicht
 * dort, wo es gemeldet wurde, und das Sonar sagt, wie weit es weg ist — nicht,
 * in welcher Richtung. Drei, vier Pings an verschiedenen Stellen, und die
 * Kreise schneiden sich dort, wo es liegt.
 */
export class SiteScene extends Phaser.Scene {
  private result!: SiteResult;
  private campaign!: Campaign;
  private phase: Phase = 'result';
  private search: SonarSearch | null = null;
  /** Wo als Naechstes gepingt oder getaucht wird, in Metern im Suchfeld. */
  private cursor: Point = { x: FIELD_METRES / 2, y: FIELD_METRES / 2 };
  private outcome: SearchOutcome = null;
  private searchClues: readonly CollectedClue[] = [];
  private divePoint: Point | null = null;
  private scroll: ScrollArea | null = null;

  constructor() {
    super('Site');
  }

  init(): void {
    this.phase = 'result';
    this.search = null;
    this.cursor = { x: FIELD_METRES / 2, y: FIELD_METRES / 2 };
    this.outcome = null;
    this.searchClues = [];
    this.divePoint = null;
    this.scroll = null;
  }

  create(): void {
    this.result = this.registry.get('missionResult') as SiteResult;
    this.campaign = this.registry.get('campaign') as Campaign;
    this.cameras.main.setBackgroundColor(PALETTE.abyss);

    const pending = this.campaign.pendingSearch;
    if (pending !== null && pending.incident.id === this.result.incident.id) {
      this.phase = 'search';
      this.search = createSonarSearch({
        seed: this.campaign.seed,
        incidentId: pending.incident.id,
        tier: pending.tier,
        submarine: pending.vesselId === 'submarine',
        seaState: this.campaign.weather.seaStateAt(pending.incident.position, this.campaign.clockHours),
        extraPings: this.campaign.rules.sonarPings,
      });
    }

    this.redraw();
    watchResize(this, () => this.redraw());
    this.bindKeys();

    this.cameras.main.fadeIn(motionMs(400), 5, 8, 13);

    audio.startDrone(DRONE_ROOT.site);
    audio.startSea();
    audio.setShallowWater(this.result.incident.depthMetres < 200);
    audio.setTension(0.85);
    audio.playMotif(SITE_MOTIF, 0.08);
    if (this.phase === 'search') {
      // Waehrend der Suche pingt nur der Spieler — ein Takt im Hintergrund
      // wuerde die eigenen Echos ueberdecken.
      audio.stopSonarSweep();
      audio.onPing = null;
    } else {
      this.startAmbientSonar();
    }

    maybeShowTutorial(this, this.campaign, 'site', { catchUp: true });
    if (this.phase === 'result') this.celebrateFinds();
  }

  private startAmbientSonar(): void {
    // Am Fundort wird gesucht — dichter getaktet als auf der Ueberfahrt.
    audio.startSonarSweep(5);
    // Direkt ueber dem Wrack: Das Echo kommt fast sofort zurueck.
    audio.onPing = () => audio.sonarEcho(0.2, false);
  }

  private celebrateFinds(): void {
    // Ein Fund bekommt ein eigenes Signal — das ist der Ertrag der Mission.
    if (this.allClues().length > 0) window.setTimeout(() => audio.cue('discovery'), 900);
  }

  private allClues(): readonly CollectedClue[] {
    return [...this.result.clues, ...this.searchClues];
  }

  private redraw(): void {
    this.scroll?.destroy();
    this.scroll = null;
    this.children.removeAll(true);

    const layout = layoutOf(this);
    this.renderTierPlate(this.result.tier, layout.width);

    if (this.phase === 'search') this.renderSearch(layout);
    else this.renderResult(layout);
  }

  /**
   * Das Stufenbild als grossflaechiger Hintergrund.
   *
   * Stark abgedunkelt: Es traegt die Stimmung, darf aber nie mit dem Text
   * konkurrieren, der darauf steht.
   */
  private renderTierPlate(tier: OutcomeTier, width: number): void {
    const key = plateKey(TIER_PLATE[tier]);
    if (!this.textures.exists(key)) return;

    const image = this.add.image(width / 2, 0, key).setOrigin(0.5, 0).setDepth(-30);
    const scale = width / image.width;
    image.setScale(scale).setAlpha(this.phase === 'search' ? 0.12 : 0.26);
  }

  // ------------------------------------------------------------------ Suche

  private renderSearch(layout: Layout): void {
    const search = this.search as SonarSearch;
    const { width, height, centreX } = layout;

    let y = 18;
    const title = this.add.text(centreX, y, t('sonar.title'), titleStyle(20)).setOrigin(0.5, 0);
    y += title.height + 6;
    const subtitle = this.add
      .text(
        centreX,
        y,
        `${t(incidentType(this.result.incident.kind).nameKey)} · ${this.result.incident.vesselName}`,
        readoutStyle(11, true),
      )
      .setOrigin(0.5, 0);
    y += subtitle.height + 8;

    const help = this.add
      .text(centreX, y, t(this.outcome === null ? 'sonar.help' : `sonar.result.${this.outcomeKey()}`), {
        ...readoutStyle(11),
        color: this.outcome === 'found' ? TEXT_COLOUR.base : PALETTE.inkDim,
        align: 'center',
        wordWrap: { width: layout.columnWidth },
      })
      .setOrigin(0.5, 0);
    y += help.height + 10;

    // Unten: Status und Knoepfe, je nach Breite neben- oder untereinander.
    const stacked = layout.compact;
    const footer = stacked ? 44 * 2 + 10 + 24 : 44 + 24;
    const statusHeight = 40;
    const fieldSize = Math.max(
      140,
      Math.min(width - layout.margin * 2, height - y - footer - statusHeight - 16),
    );
    const fieldLeft = centreX - fieldSize / 2;
    const fieldTop = y;
    const scale = fieldSize / FIELD_METRES;

    this.drawField(search, fieldLeft, fieldTop, fieldSize, scale);

    const status = this.add
      .text(centreX, fieldTop + fieldSize + 10, this.statusLine(search), {
        ...readoutStyle(11, true),
        align: 'center',
        wordWrap: { width: layout.columnWidth },
      })
      .setOrigin(0.5, 0);

    const buttonsY = Math.max(status.y + status.height + 30, height - footer + 22);
    if (this.outcome !== null) {
      createButton(this, centreX, buttonsY, t('sonar.continue'), () => this.showResult(), {
        width: 220,
      });
      return;
    }

    const actions: [string, () => void][] = [
      [t('sonar.ping', { left: search.pingsLeft }), () => this.ping()],
      [t('sonar.dive'), () => this.dive()],
    ];
    if (stacked) {
      actions.forEach(([label, onClick], index) => {
        createButton(this, centreX, buttonsY + index * 54, label, onClick, {
          width: Math.min(260, width - 32),
        });
      });
    } else {
      actions.forEach(([label, onClick], index) => {
        createButton(this, centreX + (index === 0 ? -125 : 125), buttonsY, label, onClick, {
          width: 230,
        });
      });
    }

    // Wer nicht suchen will, muss nicht — dann bleibt es beim gemeldeten Punkt.
    const skip = this.add
      .text(width - 12, 12, t('sonar.skip'), labelStyle(11, PALETTE.inkDim))
      .setOrigin(1, 0);
    skip.setInteractive({ useHandCursor: true });
    skip.on('pointerup', () => this.finishSearch('skipped'));
  }

  private statusLine(search: SonarSearch): string {
    const last = search.pings[search.pings.length - 1];
    const parts = [t('sonar.left', { count: search.pingsLeft })];
    if (last !== undefined) {
      parts.push(
        t('sonar.echo', {
          seconds: last.echoSeconds.toFixed(1),
          metres: Math.round(last.rangeMetres),
        }),
      );
    }
    return parts.join('  ·  ');
  }

  /**
   * Das Suchfeld: zwei mal zwei Seemeilen um den gemeldeten Punkt.
   *
   * Jeder Ping hinterlaesst einen Ring in der gemessenen Entfernung, so breit
   * wie die Messunsicherheit. Wer den Ton aus hat oder nicht hoert, liest
   * dieselbe Information ab — das Echo ist nur die schnellere Art, sie zu
   * bekommen.
   */
  private drawField(search: SonarSearch, left: number, top: number, size: number, scale: number): void {
    const toScreen = (p: Point): { x: number; y: number } => ({
      x: left + p.x * scale,
      y: top + p.y * scale,
    });

    const frame = this.add.rectangle(left, top, size, size, 0x061018, 0.92).setOrigin(0, 0);
    frame.setStrokeStyle(1, PALETTE.panelEdge);

    const grid = this.add.graphics();
    grid.lineStyle(1, PALETTE.panelEdge, 0.45);
    // Ein Gitter in Abstaenden von einer Viertelseemeile.
    const step = (FIELD_METRES / 8) * scale;
    for (let i = 1; i < 8; i++) {
      grid.lineBetween(left + i * step, top, left + i * step, top + size);
      grid.lineBetween(left, top + i * step, left + size, top + i * step);
    }

    // Der gemeldete Punkt in der Mitte.
    const centre = toScreen({ x: FIELD_METRES / 2, y: FIELD_METRES / 2 });
    grid.lineStyle(1, PALETTE.landmark, 0.6);
    grid.lineBetween(centre.x - 6, centre.y, centre.x + 6, centre.y);
    grid.lineBetween(centre.x, centre.y - 6, centre.x, centre.y + 6);

    /*
     * Ein Kreis, der am Feldrand endet.
     *
     * Als Linienzug statt als Kreis gezeichnet, und nur die Stuecke innerhalb
     * des Felds: Ein Ring von der Ecke aus ragte sonst weit ueber Titel und
     * Knoepfe. Eine Maske leistet dasselbe, ist in Phaser 4 aber ein
     * Renderfilter, der auf Canvas anders arbeitet als auf WebGL.
     */
    const clippedCircle = (g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number): void => {
      const inside = (x: number, y: number): boolean =>
        x >= left && x <= left + size && y >= top && y <= top + size;
      const steps = Math.max(48, Math.ceil(r / 2));
      let drawing = false;
      for (let s = 0; s <= steps; s++) {
        const a = (s / steps) * Math.PI * 2;
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r;
        if (inside(x, y)) {
          if (!drawing) {
            g.beginPath();
            g.moveTo(x, y);
            drawing = true;
          } else {
            g.lineTo(x, y);
          }
        } else if (drawing) {
          g.strokePath();
          drawing = false;
        }
      }
      if (drawing) g.strokePath();
    };

    // Die Ringe der bisherigen Pings.
    const rings = this.add.graphics();
    search.pings.forEach((ping, index) => {
      const at = toScreen(ping.at);
      const fresh = index === search.pings.length - 1;
      rings.lineStyle(Math.max(2, search.uncertaintyMetres * 2 * scale), PALETTE.course, fresh ? 0.28 : 0.16);
      clippedCircle(rings, at.x, at.y, ping.rangeMetres * scale);
      rings.lineStyle(1, PALETTE.course, fresh ? 0.9 : 0.5);
      clippedCircle(rings, at.x, at.y, ping.rangeMetres * scale);
      rings.fillStyle(PALETTE.course, 0.9);
      rings.fillCircle(at.x, at.y, 3);
    });

    // Nach dem Tauchgang: wo es wirklich lag.
    if (this.outcome !== null && this.outcome !== 'skipped') {
      const wreck = toScreen(search.wreck);
      rings.lineStyle(1.5, PALETTE.coast, 1);
      rings.strokeCircle(wreck.x, wreck.y, FIND_RADIUS_METRES * scale);
      rings.fillStyle(PALETTE.coast, 1);
      rings.fillRect(wreck.x - 3, wreck.y - 3, 6, 6);
      if (this.divePoint !== null) {
        const dive = toScreen(this.divePoint);
        rings.lineStyle(1, this.outcome === 'found' ? PALETTE.base : PALETTE.triangle, 1);
        rings.strokeCircle(dive.x, dive.y, 8);
      }
    }

    // Der Zeiger: wohin gepingt oder getaucht wird.
    if (this.outcome === null) {
      const cursor = toScreen(this.cursor);
      const marker = this.add.graphics();
      marker.lineStyle(1.5, PALETTE.vessel, 1);
      marker.strokeCircle(cursor.x, cursor.y, 10);
      marker.lineBetween(cursor.x - 16, cursor.y, cursor.x - 6, cursor.y);
      marker.lineBetween(cursor.x + 6, cursor.y, cursor.x + 16, cursor.y);
      marker.lineBetween(cursor.x, cursor.y - 16, cursor.x, cursor.y - 6);
      marker.lineBetween(cursor.x, cursor.y + 6, cursor.x, cursor.y + 16);

      frame.setInteractive({ useHandCursor: true });
      frame.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.cursor = {
          x: Math.max(0, Math.min(FIELD_METRES, (pointer.x - left) / scale)),
          y: Math.max(0, Math.min(FIELD_METRES, (pointer.y - top) / scale)),
        };
        audio.cue('select');
        this.redraw();
      });
    }

    // Massstab.
    this.add
      .text(left + 4, top + size - 4, t('sonar.scale'), labelStyle(9, PALETTE.inkFaint))
      .setOrigin(0, 1);

    // Der frische Ring laeuft einmal nach aussen — wie der Puls im Wasser.
    const last = search.pings[search.pings.length - 1];
    if (last !== undefined && this.outcome === null && !getReducedMotion() && this.animateNextPing) {
      this.animateNextPing = false;
      const at = toScreen(last.at);
      const pulse = this.add.graphics();
      const state = { r: 4, alpha: 1 };
      this.tweens.add({
        targets: state,
        r: Math.max(4, last.rangeMetres * scale),
        alpha: 0,
        // So lange, wie das Echo braucht — gestaucht, damit niemand warten muss.
        duration: Math.min(1600, last.echoSeconds * 400 + 300),
        onUpdate: () => {
          pulse.clear();
          pulse.lineStyle(2, PALETTE.course, 0.9 * state.alpha);
          clippedCircle(pulse, at.x, at.y, state.r);
        },
        onComplete: () => pulse.destroy(),
      });
    }
  }

  private animateNextPing = false;

  private ping(): void {
    const search = this.search;
    if (search === null || this.outcome !== null) return;
    const result = search.ping(this.cursor);
    if (result === null) return;

    // Der Ping und sein Echo mit der echten Laufzeit.
    audio.cue('sonar');
    audio.sonarEcho(result.rangeMetres / 1852, false);
    this.animateNextPing = true;
    this.redraw();
  }

  private dive(): void {
    const search = this.search;
    if (search === null || this.outcome !== null) return;
    const { found } = search.dive(this.cursor);
    this.divePoint = this.cursor;
    this.finishSearch(found ? 'found' : 'missed');
  }

  private finishSearch(outcome: Exclude<SearchOutcome, null>): void {
    if (this.outcome !== null) return;
    this.outcome = outcome;
    this.searchClues = this.campaign.completeSearch(outcome);

    // Jeder Ping kostet Zeit — waehrenddessen laufen alle anderen Uhren weiter.
    const pings = this.search?.pings.length ?? 0;
    if (pings > 0) this.campaign.wait(pings * HOURS_PER_PING);

    audio.cue(outcome === 'found' ? 'discovery' : 'creak');
    if (outcome === 'skipped') this.showResult();
    else this.redraw();
  }

  /**
   * Ein Treffer, der nichts einbringt, heisst nicht "mehr gefunden": Braucht der
   * laufende Akt keine Fundart, die das Wrack hergibt, sagt der Text das auch.
   */
  private outcomeKey(): string {
    if (this.outcome === 'found' && this.searchClues.length === 0) return 'found_empty';
    return this.outcome ?? 'skipped';
  }

  private showResult(): void {
    this.phase = 'result';
    this.startAmbientSonar();
    this.redraw();
    if (this.outcome !== 'found') this.celebrateFinds();
  }

  // --------------------------------------------------------------- Ergebnis

  private renderResult(layout: Layout): void {
    const result = this.result;
    const { centreX, width, height } = layout;
    const inner = layout.columnWidth;
    const left = centreX - inner / 2;

    // Alles Inhaltliche liegt in einem Scrollbereich: Mit drei Hinweisen und
    // grosser Schrift reichte die Hoehe eines Telefons nicht mehr.
    const stacked = layout.compact;
    const footer = stacked ? 44 * 2 + 10 + 28 : 44 + 32;
    this.scroll = createScrollArea(this, {
      x: left,
      y: 16,
      width: inner,
      height: height - 16 - footer,
    });
    const content = this.scroll.content;
    const mid = inner / 2;
    let y = 16;

    const add = (object: Phaser.GameObjects.Text, gap = 8): void => {
      content.add(object);
      y += object.height + gap;
    };

    add(this.add.text(mid, y, t('site.title'), titleStyle(22)).setOrigin(0.5, 0), 6);
    add(
      this.add
        .text(
          mid,
          y,
          `${t(incidentType(result.incident.kind).nameKey)} · ${result.incident.vesselName}`,
          { ...readoutStyle(12, true), align: 'center', wordWrap: { width: inner } },
        )
        .setOrigin(0.5, 0),
      12,
    );
    add(
      this.add
        .text(mid, y, `${TIER_GLYPH[result.tier]}  ${t(`outcome.${result.tier}`)}`, {
          fontFamily: FONT.display,
          fontSize: px(26),
          color: TIER_COLOUR[result.tier],
        })
        .setOrigin(0.5, 0),
      10,
    );
    add(
      this.add
        .text(mid, y, t(`outcome.${result.tier}.summary`, { count: result.rescued }), {
          ...readoutStyle(13),
          wordWrap: { width: inner },
          align: 'center',
        })
        .setOrigin(0.5, 0),
      18,
    );
    add(
      this.add
        .text(mid, y, t('site.arrived', { hours: Math.round(result.elapsedHours) }), readoutStyle(11, true))
        .setOrigin(0.5, 0),
      6,
    );
    add(
      this.add
        .text(
          mid,
          y,
          result.tier === 'rescue'
            ? t('site.rescued', { count: result.rescued })
            : t('site.lost', { count: result.souls }),
          {
            ...readoutStyle(12),
            color: result.tier === 'rescue' ? TIER_COLOUR.rescue : PALETTE.inkDim,
            align: 'center',
            wordWrap: { width: inner },
          },
        )
        .setOrigin(0.5, 0),
      12,
    );

    if (result.beyondWorkingDepth) {
      add(
        this.add
          .text(mid, y, t('site.depth.blocked'), {
            ...readoutStyle(11),
            color: TEXT_COLOUR.landmark,
            fontStyle: 'italic',
            align: 'center',
            wordWrap: { width: inner },
          })
          .setOrigin(0.5, 0),
        12,
      );
    }

    if (this.outcome !== null) {
      add(
        this.add
          .text(mid, y, t(`sonar.summary.${this.outcomeKey()}`), {
            ...readoutStyle(11),
            color: this.outcome === 'found' ? TEXT_COLOUR.base : PALETTE.inkDim,
            fontStyle: 'italic',
            align: 'center',
            wordWrap: { width: inner },
          })
          .setOrigin(0.5, 0),
        12,
      );
    }

    y += 8;
    add(this.add.text(0, y, t('site.evidence.title'), labelStyle(11)), 8);

    const clues = this.allClues();
    if (clues.length === 0) {
      add(
        this.add.text(0, y, t('site.evidence.none'), {
          ...readoutStyle(12),
          color: PALETTE.inkFaint,
          fontStyle: 'italic',
          wordWrap: { width: inner },
        }),
      );
    } else {
      for (const clue of clues) {
        add(
          this.add.text(0, y, `— ${t(clue.textKey, clue.params ?? {})}`, {
            ...readoutStyle(12),
            wordWrap: { width: inner },
          }),
          12,
        );
      }
    }

    this.scroll.finish();

    // Zwei Wege zurueck ins Spiel — und das ist die eigentliche Entscheidung
    // dieser Szene. Auf Position bleiben spart die halbe Ueberfahrt zum
    // naechsten Notruf, kostet aber jeden Nachschub: kein Treibstoff, keine
    // Werft, kein Speicherpunkt, bis man wieder daheim ist.
    const buttonWidth = stacked ? Math.min(280, width - 32) : 210;
    const stayAt = stacked ? { x: centreX, y: height - footer + 30 } : { x: centreX - 120, y: height - 46 };
    const homeAt = stacked ? { x: centreX, y: stayAt.y + 54 } : { x: centreX + 120, y: height - 46 };
    createButton(this, stayAt.x, stayAt.y, t('site.stay'), () => this.leave(false), { width: buttonWidth });
    createButton(this, homeAt.x, homeAt.y, t('site.head_home'), () => this.leave(true), {
      width: buttonWidth,
    });
  }

  private leave(homebound: boolean): void {
    audio.stopSonarSweep();
    if (homebound) {
      // Die Heimfahrt ist eine Fahrt wie jede andere und liegt deshalb in der
      // Kampagne — das andere Fahrzeug kann derweil weiterfahren.
      this.campaign.dispatch({
        vesselId: this.result.vesselId,
        incident: null,
        speed: 'cruise',
        homebound: true,
      });
    }
    this.scene.start('WorldMap');
  }

  /**
   * Tastatur: In der Suche bewegen die Pfeiltasten den Zeiger, P pingt,
   * T oder D taucht. Im Ergebnis bleibt man mit B und faehrt mit H heim.
   */
  private bindKeys(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (this.phase === 'search' && this.outcome === null) {
        const step = FIELD_METRES / 16;
        const moves: Record<string, [number, number]> = {
          arrowleft: [-step, 0],
          arrowright: [step, 0],
          arrowup: [0, -step],
          arrowdown: [0, step],
        };
        const move = moves[key];
        if (move !== undefined) {
          this.cursor = {
            x: Math.max(0, Math.min(FIELD_METRES, this.cursor.x + move[0])),
            y: Math.max(0, Math.min(FIELD_METRES, this.cursor.y + move[1])),
          };
          this.redraw();
        } else if (key === 'p' || key === ' ') {
          this.ping();
        } else if (key === 't' || key === 'd') {
          this.dive();
        }
      } else if (this.phase === 'search') {
        if (key === 'enter' || key === ' ') this.showResult();
      } else if (key === 'b' || key === 's') {
        this.leave(false);
      } else if (key === 'h') {
        this.leave(true);
      }
    });
  }
}
